import dayjs from 'utils/dayjs'

import { SegmentPeriod } from 'types/GlobalWidget'

import {
  TDatasetMarker,
  TDrawBandsPluginOptions,
  TMarkersMap
} from '../chartjs-plugins'
import { TChartBand, TChartMarker } from '../types'

// first point that ends on or after the date's day; -1 outside the chart
const getDateIndex = (date: string, periods: (SegmentPeriod | null)[]) => {
  const day = dayjs(date).format('YYYY-MM-DD')
  const first = periods.find((period) => period)

  if (!first || day < first.to_date) return -1

  return periods.findIndex((period) => period && period.to_date >= day)
}

/**
 * Resolves chart markers to the per-index map the drawMarkers plugin reads.
 * A dated marker sits on the first point that ends on or after its day.
 */
export const resolveMarkers = (
  markers: TChartMarker[] | undefined,
  periods: (SegmentPeriod | null)[] | undefined
): TMarkersMap | undefined => {
  if (!markers) return undefined

  const result: TMarkersMap = {}

  markers.forEach(({ date, index, ...marker }) => {
    const resolvedIndex =
      date !== undefined ? getDateIndex(date, periods ?? []) : index ?? -1

    if (resolvedIndex < 0) return

    const resolved: TDatasetMarker = { ...marker, index: resolvedIndex }
    const existing = result[resolvedIndex]

    result[resolvedIndex] = existing
      ? [...(Array.isArray(existing) ? existing : [existing]), resolved]
      : resolved
  })

  return result
}

/**
 * Resolves bands to the points they span, cut to the chart. A band that lies
 * wholly outside the chart is dropped.
 */
export const resolveBands = (
  bands: TChartBand[] | undefined,
  periods: (SegmentPeriod | null)[] | undefined,
  defaultColor: string
): NonNullable<TDrawBandsPluginOptions['bands']> => {
  const points = (periods ?? []).flatMap((period, index) =>
    period ? [{ index, to: period.to_date }] : []
  )

  if (!bands || points.length === 0) return []

  const first = points[0]
  const last = points[points.length - 1]

  // first point that ends on or after the day, or the last point
  const getIndex = (day: string) =>
    (points.find((point) => point.to >= day) ?? last).index

  return bands.flatMap(({ color, from, to }) => {
    const fromDay = dayjs(from).format('YYYY-MM-DD')
    const toDay = dayjs(to).format('YYYY-MM-DD')

    if (toDay < first.to || fromDay > last.to) return []

    return [
      {
        color: color ?? defaultColor,
        fromIndex: getIndex(fromDay),
        toIndex: getIndex(toDay)
      }
    ]
  })
}
